import { useState, useEffect, useRef } from "react";
import { Upload, Download, Mic, Square, Trash2, Plus, Loader2, Check, PackagePlus } from "lucide-react";
import { getInventory, saveInventoryItems, deleteInventoryItem, uploadInventoryFile, inventoryTemplateUrl, parseInventoryText, parseInventoryVoice, purchaseInventory } from "@/lib/api";

const EMPTY = { name: "", unit: "", rate: "", cost_price: "", gst_rate: "", stock: "" };

function toPayload(rows) {
  return rows
    .filter((r) => r.name && r.name.trim() && r.rate !== "" && r.rate != null)
    .map((r) => ({
      name: r.name.trim(), unit: r.unit || null, rate: Number(r.rate) || 0,
      cost_price: r.cost_price === "" || r.cost_price == null ? null : Number(r.cost_price),
      gst_rate: r.gst_rate === "" || r.gst_rate == null ? null : Number(r.gst_rate),
      stock: r.stock === "" || r.stock == null ? null : Number(r.stock),
    }));
}

export default function InventoryTab() {
  const [data, setData] = useState(null);
  const [newItem, setNewItem] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [preview, setPreview] = useState(null);
  const [voiceText, setVoiceText] = useState("");
  const [recording, setRecording] = useState(false);
  const [purchasingId, setPurchasingId] = useState(null);
  const [purchaseQty, setPurchaseQty] = useState("");
  const [purchaseCost, setPurchaseCost] = useState("");
  const fileRef = useRef(null);
  const recRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await getInventory();
      setData(data);
    } catch (e) {
      console.error("inventory load failed", e);
    }
  };
  useEffect(() => { load(); }, []);

  const flash = (t) => { setMsg(t); setTimeout(() => setMsg(""), 4000); };

  const addItem = async () => {
    const payload = toPayload([newItem]);
    if (!payload.length) return;
    setBusy(true);
    try {
      await saveInventoryItems(payload);
      setNewItem(EMPTY);
      flash("✅ Item add ho gaya");
      load();
    } catch (e) { flash(e?.response?.data?.detail || "Save nahi hua"); } finally { setBusy(false); }
  };

  const updateItem = async (it, field, value) => {
    const payload = toPayload([{ ...it, [field]: value }]);
    if (!payload.length) return;
    try { await saveInventoryItems(payload); load(); } catch (e) { flash("Update nahi hua"); }
  };

  const remove = async (it) => {
    try { await deleteInventoryItem(it.id); load(); } catch (e) { flash("Delete nahi hua"); }
  };

  const addPurchase = async (it) => {
    const qty = Number(purchaseQty);
    if (!qty || qty <= 0) return;
    setBusy(true);
    try {
      const cost = Number(purchaseCost);
      const { data } = await purchaseInventory([{
        name: it.name, qty, unit: it.unit || null,
        cost_price: cost > 0 ? cost : null,
      }]);
      flash(data.lines?.[0] || "✅ Stock add ho gaya");
      setPurchasingId(null);
      setPurchaseQty("");
      setPurchaseCost("");
      load();
    } catch (e) { flash(e?.response?.data?.detail || "Stock add nahi hua"); } finally { setBusy(false); }
  };

  const onFile = async (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try {
      const { data } = await uploadInventoryFile(f);
      flash(`✅ ${data.count} items upload hue${data.skipped ? ` (${data.skipped} skip)` : ""}`);
      load();
    } catch (ex) { flash(ex?.response?.data?.detail || "Upload nahi hua"); } finally { setBusy(false); }
  };

  const parseText = async () => {
    if (voiceText.trim().length < 2) return;
    setBusy(true);
    try {
      const { data } = await parseInventoryText(voiceText);
      setPreview(data.items);
    } catch (ex) { flash(ex?.response?.data?.detail || "Samajh nahi paye"); } finally { setBusy(false); }
  };

  const toggleRecord = async () => {
    if (recording) { recRef.current?.stop(); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      const chunks = [];
      mr.ondataavailable = (e) => chunks.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        setBusy(true);
        try {
          const { data } = await parseInventoryVoice(new Blob(chunks, { type: "audio/webm" }));
          setVoiceText(data.transcript);
          setPreview(data.items);
        } catch (ex) { flash(ex?.response?.data?.detail || "Awaaz samajh nahi aayi"); } finally { setBusy(false); }
      };
      mr.start();
      recRef.current = mr;
      setRecording(true);
    } catch { flash("Mic allow karo browser mein"); }
  };

  const confirmPreview = async () => {
    setBusy(true);
    try {
      await saveInventoryItems(preview);
      setPreview(null);
      setVoiceText("");
      flash("✅ Items add ho gaye");
      load();
    } catch { flash("Save nahi hua"); } finally { setBusy(false); }
  };

  if (!data) return <p className="text-xs text-gray-400 p-4">Loading...</p>;
  const threshold = data.low_stock_threshold ?? 5;

  return (
    <div className="space-y-3" data-testid="inventory-tab">
      <div className="flex flex-wrap items-center gap-2">
        <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" onChange={onFile} className="hidden" data-testid="inventory-file-input" />
        <button data-testid="inventory-upload-btn" onClick={() => fileRef.current?.click()} disabled={busy}
          className="inline-flex items-center gap-1.5 bg-[#006D5B] text-white text-[11px] font-bold px-3 py-1.5 rounded-full disabled:opacity-50">
          <Upload size={12} /> CSV/Excel upload
        </button>
        <a data-testid="inventory-template-link" href={inventoryTemplateUrl()}
          className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#006D5B] underline">
          <Download size={11} /> Sample template
        </a>
      </div>

      <div className="border border-gray-200 rounded-xl p-2.5 bg-white/70">
        <p className="text-[10px] text-gray-400 mb-1.5">Bol kar ya likh kar add karo — "Chawal 50 rupaye kilo, 100 kilo stock"</p>
        <div className="flex items-center gap-1.5">
          <input data-testid="inventory-voice-text" value={voiceText} onChange={(e) => setVoiceText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && parseText()}
            placeholder="Items likho ya mic dabao..."
            className="flex-1 min-w-0 border border-gray-200 rounded-full px-3 py-1.5 text-[11px] outline-none focus:ring-1 focus:ring-[#006D5B]" />
          <button data-testid="inventory-mic-btn" onClick={toggleRecord} disabled={busy}
            className={`p-2 rounded-full ${recording ? "bg-red-500 text-white animate-pulse" : "bg-gray-100 text-gray-600"}`}>
            {recording ? <Square size={13} /> : <Mic size={13} />}
          </button>
          <button data-testid="inventory-parse-btn" onClick={parseText} disabled={busy || voiceText.trim().length < 2}
            className="bg-[#006D5B] text-white text-[10px] font-bold px-3 py-1.5 rounded-full disabled:opacity-40">
            {busy ? <Loader2 size={12} className="animate-spin" /> : "Add"}
          </button>
        </div>
      </div>

      {preview && (
        <div data-testid="inventory-preview" className="border border-amber-300 bg-amber-50/60 rounded-xl p-2.5">
          <p className="text-[11px] font-bold text-gray-700 mb-1.5">Yeh items mile — sahi hai?</p>
          {preview.map((p, i) => (
            <p key={`${p.name}-${i}`} className="text-[11px] font-mono text-gray-600">
              • {p.name} — ₹{p.rate}{p.unit ? `/${p.unit}` : ""}{p.gst_rate != null ? ` · GST ${p.gst_rate}%` : ""}{p.stock != null ? ` · stock ${p.stock}` : ""}
            </p>
          ))}
          <div className="flex gap-2 mt-2">
            <button data-testid="inventory-preview-confirm" onClick={confirmPreview} disabled={busy}
              className="inline-flex items-center gap-1 bg-[#006D5B] text-white text-[10px] font-bold px-3 py-1 rounded-full"><Check size={11} /> Save karo</button>
            <button data-testid="inventory-preview-cancel" onClick={() => setPreview(null)}
              className="text-[10px] text-gray-500 underline">Cancel</button>
          </div>
        </div>
      )}

      {msg && <p data-testid="inventory-msg" className="text-[11px] text-gray-600">{msg}</p>}

      <div className="border border-gray-200 rounded-xl overflow-hidden bg-white/70">
        <div className="grid grid-cols-[1fr_50px_50px_34px_48px_46px] gap-1 px-2.5 py-1.5 bg-gray-50 text-[9px] font-bold text-gray-400 uppercase tracking-wide">
          <span>Item</span><span>Cost ₹</span><span>Sell ₹</span><span>GST%</span><span>Stock</span><span></span>
        </div>
        {data.items.length === 0 && (
          <p data-testid="inventory-empty" className="text-[11px] text-gray-400 p-3">Koi item nahi — upload karo ya bol kar add karo. Bill banate waqt AI inhi rates se price uthayega.</p>
        )}
        {data.items.map((it) => {
          const low = it.stock != null && it.stock <= threshold;
          const margin = it.cost_price > 0 && it.rate > 0
            ? Math.round(((it.rate - it.cost_price) / it.cost_price) * 100) : null;
          return (
            <div key={`${it.id}-${it.stock}-${it.rate}-${it.cost_price}`}>
              <div data-testid="inventory-row"
                className={`grid grid-cols-[1fr_50px_50px_34px_48px_46px] gap-1 items-center px-2.5 py-1.5 border-t border-gray-100 text-[11px] ${low ? "bg-red-50/60" : ""}`}>
                <span className="min-w-0">
                  <span className="font-semibold truncate block leading-tight">{it.name}{low && <span data-testid="low-stock-badge" className="ml-1 text-[8px] text-red-500 font-bold">LOW</span>}</span>
                  <span className="text-[8px] text-gray-400 leading-tight">
                    {it.unit || ""}
                    {margin != null && (
                      <span data-testid="inventory-margin" className={`${it.unit ? "ml-1" : ""} font-bold ${margin >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                        {margin >= 0 ? "+" : ""}{margin}% margin
                      </span>
                    )}
                  </span>
                </span>
                <input defaultValue={it.cost_price ?? ""} type="number" min="0" step="any" data-testid="inventory-cost-input"
                  onBlur={(e) => e.target.value !== String(it.cost_price ?? "") && updateItem(it, "cost_price", e.target.value)}
                  placeholder="-" className="w-full border border-gray-200 rounded px-1 py-0.5 font-mono text-[10px] text-gray-500" />
                <input defaultValue={it.rate} type="number" min="0" step="any" data-testid="inventory-rate-input"
                  onBlur={(e) => Number(e.target.value) !== it.rate && updateItem(it, "rate", e.target.value)}
                  className="w-full border border-gray-200 rounded px-1 py-0.5 font-mono text-[10px]" />
                <span className="text-gray-500 font-mono">{it.gst_rate != null ? `${it.gst_rate}%` : "-"}</span>
                <input defaultValue={it.stock ?? ""} type="number" min="0" step="any" data-testid="inventory-stock-input"
                  onBlur={(e) => e.target.value !== String(it.stock ?? "") && updateItem(it, "stock", e.target.value)}
                  placeholder="-" className={`w-full border rounded px-1 py-0.5 font-mono text-[10px] ${low ? "border-red-300 text-red-600" : "border-gray-200"}`} />
                <span className="flex items-center gap-1">
                  <button data-testid="inventory-purchase-btn" title="Maal aaya — stock add karo"
                    onClick={() => { setPurchasingId(purchasingId === it.id ? null : it.id); setPurchaseQty(""); }}
                    className="text-[#006D5B] hover:text-[#00584a]"><PackagePlus size={13} /></button>
                  <button data-testid="inventory-delete-btn" onClick={() => remove(it)} className="text-gray-300 hover:text-red-500"><Trash2 size={12} /></button>
                </span>
              </div>
              {purchasingId === it.id && (
                <div data-testid="inventory-purchase-panel" className="flex flex-wrap items-center gap-1.5 px-2.5 py-1.5 bg-emerald-50/70 border-t border-emerald-100">
                  <span className="text-[10px] font-semibold text-[#006D5B]">Kitna aaya?</span>
                  <input data-testid="inventory-purchase-qty" autoFocus value={purchaseQty}
                    onChange={(e) => setPurchaseQty(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addPurchase(it)}
                    type="number" min="0" step="any" placeholder={it.unit ? `qty (${it.unit})` : "qty"}
                    className="w-16 border border-emerald-200 rounded px-1.5 py-0.5 font-mono text-[10px] bg-white" />
                  <input data-testid="inventory-purchase-cost" value={purchaseCost}
                    onChange={(e) => setPurchaseCost(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addPurchase(it)}
                    type="number" min="0" step="any" placeholder="cost ₹"
                    className="w-16 border border-emerald-200 rounded px-1.5 py-0.5 font-mono text-[10px] bg-white" />
                  <button data-testid="inventory-purchase-save" onClick={() => addPurchase(it)} disabled={busy || !purchaseQty}
                    className="bg-[#006D5B] text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full disabled:opacity-40">+ Add</button>
                  <button onClick={() => setPurchasingId(null)} className="text-[10px] text-gray-400 underline">Cancel</button>
                </div>
              )}
            </div>
          );
        })}
        <div className="grid grid-cols-[1fr_50px_50px_34px_48px_46px] gap-1 items-center px-2.5 py-1.5 border-t border-gray-200 bg-gray-50/60">
          <span className="flex gap-1 min-w-0">
            <input data-testid="inventory-new-name" value={newItem.name} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
              placeholder="Naya item" className="flex-1 min-w-0 border border-gray-200 rounded px-1.5 py-1 text-[10px]" />
            <input data-testid="inventory-new-unit" value={newItem.unit} onChange={(e) => setNewItem({ ...newItem, unit: e.target.value })}
              placeholder="kg" className="w-8 border border-gray-200 rounded px-1 py-1 text-[10px]" />
          </span>
          <input data-testid="inventory-new-cost" value={newItem.cost_price} onChange={(e) => setNewItem({ ...newItem, cost_price: e.target.value })}
            type="number" placeholder="cost" className="border border-gray-200 rounded px-1 py-1 text-[10px] font-mono" />
          <input data-testid="inventory-new-rate" value={newItem.rate} onChange={(e) => setNewItem({ ...newItem, rate: e.target.value })}
            type="number" placeholder="sell" className="border border-gray-200 rounded px-1 py-1 text-[10px] font-mono" />
          <input data-testid="inventory-new-gst" value={newItem.gst_rate} onChange={(e) => setNewItem({ ...newItem, gst_rate: e.target.value })}
            type="number" placeholder="%" className="border border-gray-200 rounded px-1 py-1 text-[10px] font-mono" />
          <input data-testid="inventory-new-stock" value={newItem.stock} onChange={(e) => setNewItem({ ...newItem, stock: e.target.value })}
            type="number" placeholder="qty" className="border border-gray-200 rounded px-1 py-1 text-[10px] font-mono" />
          <button data-testid="inventory-add-btn" onClick={addItem} disabled={busy || !newItem.name || newItem.rate === ""}
            className="text-[#006D5B] disabled:opacity-30"><Plus size={14} /></button>
        </div>
      </div>
      <p className="text-[9px] text-gray-400">Bills hamesha Sell ₹ se banenge · Maal aane pe 📦 button ya chat mein "50 kilo chawal aaya 40 rupaye kilo" bolo (wo cost price banega) · {threshold} ya kam bachne pe LOW warning</p>
    </div>
  );
}
